import * as React from 'react';
import { useMemo } from 'react';
import { Box, Button, Card, Chip, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import {
  Bank as BankIcon,
  Eye as EyeIcon,
  PencilSimple as EditIcon,
  Plus as PlusIcon,
  Trash as TrashIcon,
} from '@phosphor-icons/react';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useConfirm } from 'material-ui-confirm';
import dayjs from 'dayjs';

import type { Amenity } from '@/types/v2/amenity';
import { deleteAmenity } from '@/api/amenities';
import { useDialog } from '@/hooks/v2/use-dialog';
import { useUser } from '@/hooks/v2/use-user';
import { toast } from '@/components/core/v2/toaster';

import { CreateAmenityDialog } from './create-amenity-dialog';
import { EditAmenityDialog } from './edit-amenity-dialog';

interface AmenitiesTableProps {
  rows: Amenity[];
  loading: boolean;
  onCreated: (amenity: Amenity) => void;
}

export function AmenitiesTable({ rows, loading, onCreated }: AmenitiesTableProps): React.JSX.Element {
  const dialog = useDialog();
  const confirm = useConfirm();
  const { community } = useUser();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedAmenity, setSelectedAmenity] = React.useState<Amenity | null>(null);
  const [openCreate, setOpenCreate] = React.useState(false);
  const [openEdit, setOpenEdit] = React.useState(false);

  const deleteMutation = useMutation({
    mutationFn: (amenityId: string) => deleteAmenity(community?.id || '', amenityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['amenities', community?.id] });
      toast.success('Amenity deleted successfully');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete amenity');
    },
  });

  const columns = useMemo<MRT_ColumnDef<Amenity>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Name',
        size: 200,
      },
      {
        accessorKey: 'capacity',
        header: 'Capacity',
        size: 100,
        Cell: ({ cell }) => cell.getValue() || '—',
      },
      {
        accessorKey: 'requires_approval',
        header: 'Requires Approval',
        size: 150,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? 'Yes' : 'No'}
            size="small"
            color={cell.getValue() ? 'warning' : 'success'}
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        accessorKey: 'deposit_amount',
        header: 'Deposit',
        size: 100,
        Cell: ({ cell }) => {
          const val = cell.getValue<number | string | null | undefined>();
          const fee = typeof val === 'number' ? val : Number(val);
          return fee > 0 && !Number.isNaN(fee) ? `$${fee.toFixed(2)}` : '—';
        },
      },
      {
        accessorKey: 'total_fee_amount',
        header: 'Total Fee',
        size: 120,
        Cell: ({ cell }) => {
          const val = cell.getValue<number | string | null | undefined>();
          const fee = typeof val === 'number' ? val : Number(val);
          return fee > 0 && !Number.isNaN(fee) ? `$${fee.toFixed(2)}` : '—';
        },
      },
      {
        accessorKey: 'is_active',
        header: 'Status',
        size: 100,
        Cell: ({ cell }) => (
          <Chip
            label={cell.getValue() ? 'Active' : 'Inactive'}
            size="small"
            color={cell.getValue() ? 'success' : 'default'}
            sx={{ fontWeight: 500 }}
          />
        ),
      },
      {
        accessorKey: 'created_at',
        header: 'Created',
        size: 120,
        Cell: ({ cell }) => dayjs(cell.getValue<string>()).format('MMM D, YYYY'),
      },
    ],
    [],
  );

  const handleEdit = (amenity: Amenity) => {
    setSelectedAmenity(amenity);
    setOpenEdit(true);
  };

  const handleDelete = async (amenity: Amenity) => {
    try {
      await confirm({
        title: 'Delete Amenity',
        content: (
          <Box sx={{ p: 2 }}>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: 3,
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider'
            }}>
              <BankIcon size={32} weight="duotone" color="#ef4444" />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {amenity.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {amenity.description || 'No description'}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: 'center', p: 2, borderRadius: 2, border: '1px solid', borderColor: 'warning.main', bgcolor: 'warning.lighter' }}>
              <Typography sx={{ fontWeight: 500, color: 'warning.dark', mb: 1 }} variant="body1">
                ⚠️ Warning: This action cannot be undone
              </Typography>
              <Typography color="warning.dark" variant="body2">
                Deleting this amenity will permanently remove it and all associated reservations.
                This action cannot be reversed.
              </Typography>
            </Box>
          </Box>
        ),
        dialogProps: {
          maxWidth: 'sm',
          sx: {
            '& .MuiDialog-paper': {
              borderRadius: 3
            }
          }
        },
        confirmationText: 'Delete Amenity',
        cancellationText: 'Cancel',
        confirmationButtonProps: {
          color: 'error',
          variant: 'contained'
        },
        cancellationButtonProps: {
          variant: 'outlined'
        }
      });

      await deleteMutation.mutateAsync(amenity.id);
    } catch (error) {
      // User cancelled the confirmation
    }
  };

  const table = useMaterialReactTable<Amenity>({
    columns,
    data: rows,
    initialState: {
      showGlobalFilter: true,
      columnPinning: {
        right: ['mrt-row-actions'],
      },
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    state: {
      isLoading: loading,
    },
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => (
      <Box sx={{ display: 'flex', gap: 0.5 }}>
        <IconButton onClick={() => handleEdit(row.original)} size="small" title="Edit">
          <EditIcon fontSize="small" />
        </IconButton>
        <IconButton
          onClick={() => handleDelete(row.original)}
          size="small"
          color="error"
          title="Delete"
        >
          <TrashIcon fontSize="small" />
        </IconButton>
      </Box>
    ),
  });

  const activeCount = rows.filter((a) => a.is_active).length;
  const inactiveCount = rows.length - activeCount;
  const withFeeCount = rows.filter((a) => (a.total_fee_amount && a.total_fee_amount > 0) || (a.deposit_amount && a.deposit_amount > 0)).length;

  return (
    <>
      <Card>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            p: 2,
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ flexWrap: 'wrap', gap: 1, flex: 1 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BankIcon size={18} weight="duotone" />
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                {rows.length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total
              </Typography>
            </Box>
            <Chip label={`${activeCount} Active`} size="small" color="success" sx={{ fontWeight: 500 }} />
            <Chip
              label={`${inactiveCount} Inactive`}
              size="small"
              color="default"
              sx={{ fontWeight: 500 }}
            />
            <Chip label={`${withFeeCount} With Fee`} size="small" color="primary" sx={{ fontWeight: 500 }} />
          </Stack>

          <Button
            startIcon={<PlusIcon size={18} />}
            variant="contained"
            onClick={() => setOpenCreate(true)}
            size="small"
            sx={{ fontWeight: 500, minWidth: 'fit-content' }}
          >
            Add Amenity
          </Button>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: '0px 4px', alignItems: 'center' }}>
          <MRT_GlobalFilterTextField table={table} />
          <MRT_ToggleFiltersButton table={table} />
        </Box>

        <MaterialReactTable table={table} />
      </Card>

      <CreateAmenityDialog open={openCreate} onClose={() => setOpenCreate(false)} onSuccess={onCreated} />

      {selectedAmenity && (
        <EditAmenityDialog
          open={openEdit}
          amenity={selectedAmenity}
          onClose={() => {
            setOpenEdit(false);
            setSelectedAmenity(null);
          }}
          onSuccess={onCreated}
        />
      )}
    </>
  );
}





